import type { JSX } from "react";
import { useEffect, useState } from "react";
import { track } from "@/lib/analytics";

const STORAGE_KEY = "kotlorz.tutorialDismissed";

interface Callout {
  id: string;
  label: string;
  shortcut: string;
  hint: string;
  anchor: { top: string; left: string };
}

const CALLOUTS: Callout[] = [
  {
    id: "book",
    label: "about",
    shortcut: "A",
    hint: "Click the book to open it. The page is my about.",
    anchor: { top: "55%", left: "20%" },
  },
  {
    id: "monitor",
    label: "browser",
    shortcut: "M",
    hint: "The monitor is my browser. Projects + contact live there.",
    anchor: { top: "30%", left: "50%" },
  },
  {
    id: "pinBoard",
    label: "projects",
    shortcut: "P",
    hint: "Pin board jumps the monitor to project case studies.",
    anchor: { top: "20%", left: "78%" },
  },
  {
    id: "switch",
    label: "theme",
    shortcut: "T",
    hint: "Flip the wall switch to swap dark and light themes.",
    anchor: { top: "15%", left: "30%" },
  },
  {
    id: "phone",
    label: "contact",
    shortcut: "/",
    hint: "Pick up the phone to send me a message.",
    anchor: { top: "75%", left: "70%" },
  },
];

const isDevParam = (): boolean => {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).has("dev");
};

const shouldShow = (): boolean => {
  if (typeof window === "undefined") return false;
  if (isDevParam()) return true;
  return localStorage.getItem(STORAGE_KEY) !== "1";
};

export const FirstVisitTutorial = (): JSX.Element | null => {
  const [open, setOpen] = useState<boolean>(() => shouldShow());

  useEffect(() => {
    if (!open) return;
    track("tutorial_open");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        localStorage.setItem(STORAGE_KEY, "1");
        track("tutorial_dismiss");
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const dismiss = (): void => {
    localStorage.setItem(STORAGE_KEY, "1");
    track("tutorial_dismiss");
    setOpen(false);
  };

  if (!open) return null;

  return (
    <aside
      role="dialog"
      aria-modal="true"
      aria-label="Tutorial"
      className="absolute inset-0 z-40 bg-bg0/40 backdrop-blur-[1px]"
    >
      {CALLOUTS.map((c) => (
        <div
          key={c.id}
          data-testid="tutorial-callout"
          className="absolute -translate-x-1/2 -translate-y-1/2 rounded-sm border border-phosphor bg-bg1/95 px-3 py-2 font-mono text-xs text-phosphor"
          style={{ top: c.anchor.top, left: c.anchor.left }}
        >
          <div className="flex items-center justify-between gap-3 border-b border-rule pb-1 uppercase tracking-wider">
            <span>{c.label}</span>
            <span className="text-ink2">[{c.shortcut}]</span>
          </div>
          <p className="mt-1 max-w-[200px] text-ink1">{c.hint}</p>
        </div>
      ))}
      <div className="absolute bottom-12 left-1/2 -translate-x-1/2">
        <button
          type="button"
          onClick={dismiss}
          className="rounded-sm border border-phosphor bg-bg1 px-6 py-2 font-mono text-sm uppercase tracking-wider text-phosphor hover:bg-phosphor hover:text-bg0"
        >
          Got it
        </button>
      </div>
    </aside>
  );
};
